import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaPlus, FaMinus, FaMapMarkerAlt } from 'react-icons/fa';
import useScrollTo from '../hooks/useScrollTo';
import SEOHelmet from '../components/SEO/SEOHelmet';
import { ServiceSchema, BreadcrumbSchema, FAQSchema } from '../components/SEO/schemas';

const servicesData = {
  'custom-software-development': {
    title: 'Custom Software Development Services',
    metaTitle: 'Custom Software Development Company Minneapolis MN | CitiTekPro',
    metaDescription: 'Professional custom software development services in Minneapolis, MN. CitiTekPro builds tailored software solutions using MERN stack, cloud platforms, and modern technologies for businesses across Minnesota and the Twin Cities.',
    keywords: 'custom software development Minneapolis MN, custom software company Minnesota, enterprise software development Minneapolis, tailored software solutions Twin Cities, MERN stack development Minnesota, cloud software development Minneapolis',
    headline: 'Custom Software Development',
    subheadline: 'Tailored Solutions for Your Unique Business Needs',
    description: 'At CitiTekPro, we specialize in building custom software solutions that perfectly align with your business objectives. Our Minneapolis-based team uses modern technologies like MERN stack, cloud platforms, and AI integration to create scalable, secure, and efficient software applications for businesses across Minnesota and the Twin Cities.',
    benefits: [
      'Tailored to your specific business requirements',
      'Scalable architecture designed for future growth',
      'Modern technology stack (MERN, AI, Cloud)',
      'Full-cycle development from planning to deployment',
      'Ongoing support and maintenance',
      'Integration with your existing systems'
    ],
    faqs: [
      {
        question: 'How long does custom software development take in Minneapolis?',
        answer: 'Project timelines vary based on scope and complexity. Simple applications typically take 4–8 weeks, while enterprise-grade platforms can take 3–6 months. CitiTekPro provides a detailed project timeline during our initial consultation.'
      },
      {
        question: 'What types of custom software does CitiTekPro build for Minnesota businesses?',
        answer: 'We build a wide range of custom software for Minnesota businesses including web-based business applications, CRM systems, SaaS platforms, mobile apps, business automation tools, and data analytics dashboards — all tailored to your specific industry and workflow.'
      },
      {
        question: 'Do you provide software development services outside of Minneapolis?',
        answer: 'Yes. While based in Minneapolis at 2910 Pillsbury Ave Suite 31, CitiTekPro serves businesses throughout the Twin Cities metro, including Saint Paul, Bloomington, Brooklyn Park, Plymouth, Maple Grove, and other Minnesota communities.'
      },
      {
        question: 'What technologies do you use for custom software development?',
        answer: 'We primarily use the MERN stack (MongoDB, Express.js, React, Node.js) for web-based software, along with React Native for mobile apps, cloud platforms (AWS, Azure, GCP), and AI/ML integrations as needed.'
      }
    ],
    cta: 'Start Your Custom Software Project'
  },
  'web-development': {
    title: 'Web Development Services',
    metaTitle: 'Web Development Company Minneapolis MN | Professional Web Design Minnesota',
    metaDescription: 'Leading web development company in Minneapolis, MN. CitiTekPro creates responsive, high-performance websites and web applications for businesses across Minnesota using React, Node.js, and modern frameworks.',
    keywords: 'web development Minneapolis MN, web design Minnesota, responsive website development Twin Cities, React development Minneapolis, web application development Minnesota, Minneapolis web developers, website design Minneapolis',
    headline: 'Professional Web Development',
    subheadline: 'Modern, Responsive Websites That Drive Results',
    description: 'CitiTekPro delivers cutting-edge web development services for Minnesota businesses. We create responsive, high-performance websites and web applications that engage users and drive conversions. Our Minneapolis-based team builds with modern frameworks like React and Node.js for businesses throughout the Twin Cities and greater Minnesota.',
    benefits: [
      'Responsive design optimized for all devices',
      'Fast loading times and optimal performance',
      'SEO-optimized architecture from day one',
      'Modern UI/UX best practices and accessibility',
      'Content management system integration',
      'E-commerce capabilities and secure payment'
    ],
    faqs: [
      {
        question: 'How much does a professional website cost in Minneapolis?',
        answer: 'Website costs vary significantly based on scope, features, and complexity. A professional business website typically starts at a few thousand dollars, while complex web applications can range much higher. CitiTekPro provides transparent, detailed proposals so you know exactly what you\'re getting.'
      },
      {
        question: 'Does CitiTekPro build websites for small businesses in the Twin Cities?',
        answer: 'Absolutely. CitiTekPro builds websites for businesses of all sizes across the Twin Cities metro — from startups and small businesses in Minneapolis and Saint Paul to established enterprises throughout Minnesota.'
      },
      {
        question: 'How long does it take to build a website in Minneapolis?',
        answer: 'A standard business website typically takes 3–6 weeks. More complex sites with custom features or e-commerce can take 8–16 weeks. We work with your timeline and keep you informed throughout the process.'
      },
      {
        question: 'Will my new website be optimized for local Minneapolis searches?',
        answer: 'Yes. Every website we build includes foundational local SEO — optimized meta tags, structured data, geographic targeting, and a site architecture that helps Minneapolis and Minnesota customers find you on Google.'
      }
    ],
    cta: 'Build Your Website'
  },
  'mobile-app-development': {
    title: 'Mobile App Development Services',
    metaTitle: 'Mobile App Development Minnesota | iOS & Android Apps Minneapolis MN',
    metaDescription: 'Expert mobile app development services in Minneapolis, MN. CitiTekPro builds native and cross-platform mobile applications for iOS and Android for businesses throughout Minnesota and the Twin Cities.',
    keywords: 'mobile app development Minneapolis MN, iOS app development Minnesota, Android app development Twin Cities, cross-platform mobile apps Minnesota, React Native development Minneapolis, mobile application development Minnesota',
    headline: 'Mobile App Development',
    subheadline: 'Native and Cross-Platform Mobile Solutions',
    description: 'Transform your business with custom mobile applications built by CitiTekPro\'s Minneapolis-based development team. We develop powerful apps for iOS and Android platforms, delivering exceptional user experiences and robust functionality for Minnesota businesses of all sizes.',
    benefits: [
      'Native iOS and Android development',
      'Cross-platform solutions with React Native',
      'Intuitive user interface design',
      'Offline functionality support',
      'App Store and Google Play optimization',
      'Push notifications and integrated analytics'
    ],
    faqs: [
      {
        question: 'How much does it cost to develop a mobile app in Minneapolis?',
        answer: 'Mobile app costs depend on platform (iOS, Android, or both), features, and complexity. Simple apps can start from a few thousand dollars while full-featured enterprise apps range significantly higher. CitiTekPro provides detailed estimates after a free discovery session.'
      },
      {
        question: 'Do you build apps for both iPhone and Android in Minnesota?',
        answer: 'Yes. We develop native iOS and Android apps, as well as cross-platform solutions using React Native that work on both platforms from a single codebase — often the most cost-effective approach for Minnesota businesses.'
      },
      {
        question: 'Can you help publish my app to the App Store from Minneapolis?',
        answer: 'Absolutely. CitiTekPro handles the full App Store and Google Play submission process, including store optimization (ASO), screenshots, descriptions, and compliance review — so you can launch confidently.'
      }
    ],
    cta: 'Build Your Mobile App'
  },
  'seo-services': {
    title: 'SEO Services Minnesota',
    metaTitle: 'SEO Services Minneapolis MN | Local SEO Minnesota & Twin Cities',
    metaDescription: 'Professional SEO services for Minneapolis and Minnesota businesses. CitiTekPro provides local SEO, technical SEO, and digital marketing to improve your search rankings in Minneapolis, Saint Paul, and across the Twin Cities.',
    keywords: 'SEO services Minneapolis MN, local SEO Minneapolis, search engine optimization Minnesota, Minnesota SEO company, Twin Cities SEO, digital marketing Minneapolis, local search optimization Minnesota',
    headline: 'SEO & Digital Marketing Services',
    subheadline: 'Get Found by Your Target Customers in Minneapolis & Beyond',
    description: 'Boost your online visibility with CitiTekPro\'s comprehensive SEO services. We help Minneapolis and Minnesota businesses rank higher in search results, attract more qualified traffic, and convert visitors into customers through proven local and technical SEO strategies.',
    benefits: [
      'Local SEO targeting Minneapolis and Minnesota',
      'Technical SEO audit and optimization',
      'Keyword research and content strategy',
      'Google Business Profile optimization',
      'Link building and off-page SEO',
      'Monthly reporting and performance analytics'
    ],
    faqs: [
      {
        question: 'How long does SEO take to show results for Minneapolis businesses?',
        answer: 'SEO is a long-term strategy. Most Minneapolis businesses begin seeing meaningful improvements in rankings and traffic within 3–6 months. Highly competitive keywords may take longer, while local Minneapolis searches often show faster results.'
      },
      {
        question: 'What is local SEO and why does it matter for Twin Cities businesses?',
        answer: 'Local SEO optimizes your business to appear in location-based searches like "web developer Minneapolis" or "IT consulting near me." For Twin Cities businesses, local SEO is critical to capturing customers who are actively searching for services in your area.'
      },
      {
        question: 'Can CitiTekPro help my Minneapolis business rank on Google Maps?',
        answer: 'Yes. We optimize your Google Business Profile and implement local SEO signals including consistent NAP information, local citations, and schema markup — all key factors for ranking in Google\'s local map pack for Minneapolis and surrounding areas.'
      },
      {
        question: 'Do you provide SEO for businesses outside Minneapolis?',
        answer: 'Yes. CitiTekPro provides SEO services to businesses throughout Minnesota including Saint Paul, Bloomington, Brooklyn Park, Maple Grove, Eagan, Edina, and the greater Twin Cities metro area.'
      }
    ],
    cta: 'Improve Your Rankings'
  },
  'it-consulting': {
    title: 'IT Consulting Services',
    metaTitle: 'IT Consulting Services Minneapolis MN | Technology Consulting Minnesota',
    metaDescription: 'Strategic IT consulting services for Minneapolis and Minnesota businesses. CitiTekPro provides expert technology consulting, digital transformation, and IT strategy services across the Twin Cities.',
    keywords: 'IT consulting Minneapolis MN, technology consulting Minnesota, IT strategy services Twin Cities, digital transformation consulting Minneapolis, IT advisory services Minnesota, managed IT consulting Minneapolis',
    headline: 'IT Consulting & Strategy',
    subheadline: 'Strategic Technology Guidance for Business Growth',
    description: 'Make informed technology decisions with CitiTekPro\'s IT consulting services. Our Minneapolis-based technology consultants help businesses across Minnesota develop effective IT strategies, optimize infrastructure, and leverage technology to achieve their objectives and stay competitive.',
    benefits: [
      'Technology roadmap development',
      'Digital transformation strategy',
      'Infrastructure audit and optimization',
      'Security and compliance assessment',
      'Vendor selection and management',
      'Cost optimization and ROI analysis'
    ],
    faqs: [
      {
        question: 'What does an IT consultant do for small businesses in Minneapolis?',
        answer: 'An IT consultant helps small Minneapolis businesses make smart technology decisions — from selecting the right software and hardware to planning infrastructure upgrades, improving cybersecurity, and aligning technology investments with business goals.'
      },
      {
        question: 'How does IT consulting help Minnesota businesses stay competitive?',
        answer: 'Technology evolves rapidly. CitiTekPro helps Minnesota businesses evaluate emerging tools, automate inefficient processes, modernize aging systems, and build IT strategies that support growth while managing costs and security risks.'
      },
      {
        question: 'Does CitiTekPro offer ongoing IT consulting or just one-time projects?',
        answer: 'We offer both. Many Minneapolis and Twin Cities clients engage CitiTekPro for one-time technology audits or strategy sessions, while others maintain ongoing advisory relationships for continuous technology guidance and support.'
      }
    ],
    cta: 'Schedule a Consultation'
  },
  'business-automation': {
    title: 'Business Automation Solutions',
    metaTitle: 'Business Automation Services Minneapolis MN | Workflow Automation Minnesota',
    metaDescription: 'Business process automation services for Minneapolis and Minnesota companies. CitiTekPro implements email automation, reputation management automation, financial process automation, and workflow optimization across the Twin Cities.',
    keywords: 'business automation Minneapolis MN, workflow automation Minnesota, email marketing automation Twin Cities, reputation management automation, financial process automation Minneapolis, business process optimization Minnesota',
    headline: 'Business Process Automation',
    subheadline: 'Streamline Operations & Boost Efficiency',
    description: 'Eliminate manual tasks and optimize your workflows with CitiTekPro\'s business automation solutions. We help Minneapolis and Minnesota businesses automate email marketing, reputation management, financial processes, and repetitive workflows — reducing costs and increasing productivity.',
    benefits: [
      'Email marketing automation with personalized campaigns',
      'Reputation management and review generation automation',
      'Financial billing and payment reminder automation',
      'Integration with your existing business systems',
      'Real-time reporting and performance analytics',
      'Scalable automation that grows with your business'
    ],
    faqs: [
      {
        question: 'What business processes can CitiTekPro automate for Minneapolis companies?',
        answer: 'CitiTekPro automates a wide range of processes for Minneapolis businesses including email marketing campaigns, customer review requests, billing reminders, lead follow-up sequences, reporting workflows, and more — tailored to your specific operations.'
      },
      {
        question: 'How quickly can business automation improve efficiency for Twin Cities businesses?',
        answer: 'Many clients see measurable efficiency gains within the first month after automation implementation. The exact impact depends on which processes are automated and their current volume, but reducing manual data entry and follow-up tasks alone often delivers immediate time savings.'
      },
      {
        question: 'Can business automation integrate with my existing software in Minneapolis?',
        answer: 'Yes. CitiTekPro designs automation solutions to integrate with the tools you already use — CRM systems, email platforms, accounting software, and more — so you get the benefits of automation without abandoning your existing workflow.'
      }
    ],
    cta: 'Automate Your Business'
  },
  'ai-software-development': {
    title: 'AI Software Development',
    metaTitle: 'AI Software Development Minneapolis MN | Machine Learning Solutions Minnesota',
    metaDescription: 'Artificial intelligence and machine learning development services in Minneapolis, MN. CitiTekPro builds AI-powered applications and intelligent automation solutions for businesses across Minnesota and the Twin Cities.',
    keywords: 'AI software development Minneapolis MN, machine learning Minnesota, artificial intelligence development Twin Cities, AI integration services Minneapolis, intelligent automation Minnesota, AI solutions Minneapolis',
    headline: 'AI & Machine Learning Development',
    subheadline: 'Harness the Power of Artificial Intelligence',
    description: 'Stay ahead of the competition with CitiTekPro\'s AI software development services. Our Minneapolis-based team integrates artificial intelligence and machine learning into your business applications to automate complex tasks, gain actionable insights, and deliver intelligent user experiences for Minnesota businesses.',
    benefits: [
      'Custom AI/ML model development and training',
      'Natural language processing (NLP) solutions',
      'Computer vision and image recognition',
      'Predictive analytics and business intelligence',
      'AI-powered chatbots and virtual assistants',
      'Seamless integration with existing systems'
    ],
    faqs: [
      {
        question: 'What AI solutions can help Minneapolis small businesses?',
        answer: 'Minneapolis small businesses benefit from AI in many ways including intelligent chatbots for customer service, predictive analytics for inventory and sales forecasting, automated email personalization, and AI-powered tools that reduce manual work across operations.'
      },
      {
        question: 'How does CitiTekPro implement AI for Minnesota businesses?',
        answer: 'We begin with a discovery session to understand your business challenges, then identify where AI delivers the highest ROI. Our Minneapolis team handles data preparation, model development, integration, and training so your team can use AI effectively from day one.'
      }
    ],
    cta: 'Explore AI Solutions'
  },
  'mern-stack-development': {
    title: 'MERN Stack Development',
    metaTitle: 'MERN Stack Development Minneapolis MN | MongoDB Express React Node.js Minnesota',
    metaDescription: 'Expert MERN stack development services in Minneapolis, MN. CitiTekPro builds modern web applications using MongoDB, Express, React, and Node.js for businesses across Minnesota and the Twin Cities.',
    keywords: 'MERN stack development Minneapolis MN, React development Minnesota, Node.js development Twin Cities, MongoDB development Minneapolis, full-stack JavaScript development Minnesota, MERN developer Minneapolis',
    headline: 'MERN Stack Development',
    subheadline: 'Full-Stack JavaScript Solutions for Modern Business',
    description: 'Build powerful, scalable web applications with CitiTekPro\'s MERN stack expertise. Our Minneapolis-based developers leverage MongoDB, Express.js, React, and Node.js to create modern, efficient, and maintainable full-stack JavaScript applications for businesses throughout Minnesota.',
    benefits: [
      'Full-stack JavaScript development (MERN)',
      'Single-page applications (SPAs) with React',
      'RESTful and GraphQL API development',
      'Real-time application features with WebSockets',
      'Scalable cloud deployment and DevOps',
      'Agile, iterative development practices'
    ],
    faqs: [
      {
        question: 'Why choose MERN stack for your Minneapolis business application?',
        answer: 'The MERN stack offers a unified JavaScript environment for both frontend and backend development, resulting in faster development, easier maintenance, and excellent performance. It\'s ideal for modern business web applications, real-time dashboards, and SaaS platforms.'
      },
      {
        question: 'Does CitiTekPro build MERN applications for startups in the Twin Cities?',
        answer: 'Yes. CitiTekPro works with startups and established businesses alike across the Twin Cities. We help Minneapolis startups build MVPs quickly and efficiently, then scale their MERN applications as their user base grows.'
      }
    ],
    cta: 'Start Your MERN Project'
  },
  'saas-development': {
    title: 'SaaS Application Development',
    metaTitle: 'SaaS Development Company Minneapolis MN | Cloud Software Minnesota',
    metaDescription: 'Professional SaaS application development services in Minneapolis, MN. CitiTekPro builds scalable cloud-based software as a service solutions for businesses across Minnesota and the Twin Cities.',
    keywords: 'SaaS development Minneapolis MN, cloud software development Minnesota, software as a service development Twin Cities, multi-tenant SaaS Minneapolis, SaaS platform development Minnesota',
    headline: 'SaaS Application Development',
    subheadline: 'Build Scalable Cloud-Based Software',
    description: 'Launch your software as a service platform with CitiTekPro. Our Minneapolis-based team specializes in building scalable, multi-tenant SaaS applications with subscription management, user analytics, and all the features you need to succeed in the cloud software market across Minnesota and beyond.',
    benefits: [
      'Multi-tenant SaaS architecture',
      'Subscription billing and payment integration',
      'User management, roles, and authentication',
      'Analytics and reporting dashboards',
      'API development for third-party integrations',
      'Scalable cloud infrastructure and DevOps'
    ],
    faqs: [
      {
        question: 'How much does it cost to build a SaaS application in Minneapolis?',
        answer: 'SaaS development costs vary widely based on features, integrations, and scale. A minimum viable SaaS product (MVP) typically starts in the tens of thousands of dollars. CitiTekPro can help you prioritize features to launch efficiently and grow from there.'
      },
      {
        question: 'Can CitiTekPro build a SaaS product for a Minnesota startup?',
        answer: 'Absolutely. We work with Minnesota entrepreneurs and startups to design and build SaaS platforms from the ground up — from product strategy and UI design to backend development, cloud deployment, and post-launch support.'
      }
    ],
    cta: 'Build Your SaaS Platform'
  },
  'custom-crm-development': {
    title: 'Custom CRM Development',
    metaTitle: 'Custom CRM Development Minneapolis MN | CRM Software Minnesota',
    metaDescription: 'Custom CRM development services for Minneapolis and Minnesota businesses. CitiTekPro builds tailored customer relationship management systems for companies in the Twin Cities and throughout Minnesota.',
    keywords: 'custom CRM development Minneapolis MN, CRM software development Minnesota, customer relationship management Twin Cities, tailored CRM solutions Minneapolis, CRM integration services Minnesota',
    headline: 'Custom CRM Development',
    subheadline: 'Manage Customer Relationships Effectively',
    description: 'Get a CRM system built specifically for your business processes. CitiTekPro develops custom customer relationship management solutions that help Minneapolis and Minnesota businesses track leads, manage customer interactions, automate follow-ups, and drive sales growth.',
    benefits: [
      'CRM tailored to your specific sales process',
      'Contact and lead management',
      'Sales pipeline visualization and tracking',
      'Customer communication history and notes',
      'Reporting and business intelligence',
      'Integration with existing tools and platforms'
    ],
    faqs: [
      {
        question: 'Why build a custom CRM instead of using off-the-shelf software in Minneapolis?',
        answer: 'Off-the-shelf CRM systems often require expensive monthly subscriptions and force you to adapt your process to their workflow. A custom CRM built by CitiTekPro works exactly how your Minneapolis business operates, with no unnecessary features and no recurring license fees.'
      },
      {
        question: 'Can CitiTekPro integrate a custom CRM with my existing tools?',
        answer: 'Yes. We build custom CRM systems that integrate with the tools your Minnesota business already uses — email platforms, accounting software, marketing automation, calendar systems, and more — creating a unified view of your customer relationships.'
      },
      {
        question: 'How long does it take to build a custom CRM for a Twin Cities business?',
        answer: 'A core custom CRM for a small to mid-sized Twin Cities business typically takes 8–16 weeks to design, build, test, and deploy. Timeline depends on the number of features, integrations, and data migration requirements.'
      }
    ],
    cta: 'Build Your CRM'
  }
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors duration-200"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-gray-900 dark:text-white pr-4">{question}</span>
        {isOpen
          ? <FaMinus className="text-primary dark:text-teal flex-shrink-0" />
          : <FaPlus className="text-primary dark:text-teal flex-shrink-0" />
        }
      </button>
      {isOpen && (
        <div className="px-5 pb-5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const ServiceLandingPage = () => {
  const { slug } = useParams();
  const { scrollToSection } = useScrollTo();
  const service = servicesData[slug];

  if (!service) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <SEOHelmet
        title={service.metaTitle}
        description={service.metaDescription}
        keywords={service.keywords}
        canonicalUrl={`https://cititekpro.com/services/${slug}`}
        ogType="service"
      />
      <ServiceSchema
        service={{
          name: service.title,
          description: service.metaDescription,
          url: `https://cititekpro.com/services/${slug}`,
          serviceType: 'Professional Service',
          areaServed: ['Minnesota', 'Minneapolis', 'Saint Paul', 'Bloomington', 'Brooklyn Park']
        }}
      />
      <BreadcrumbSchema
        breadcrumbs={[
          { name: 'Home', url: 'https://cititekpro.com/' },
          { name: 'Services', url: 'https://cititekpro.com/#services' },
          { name: service.title, url: `https://cititekpro.com/services/${slug}` }
        ]}
      />
      {service.faqs && <FAQSchema faqs={service.faqs} />}

      <div className="relative pt-16 min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 -left-40 w-96 h-96 bg-primary/5 dark:bg-teal/5 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-40 -right-40 w-96 h-96 bg-teal/5 dark:bg-primary/5 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          {/* Page Header */}
          <div className="text-center mb-12 md:mb-16 animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-gray-900 dark:text-white">{service.headline}</span>
            </h1>
            <p className="text-xl md:text-2xl text-primary dark:text-teal font-semibold mb-6">
              {service.subheadline}
            </p>
            <div className="h-1.5 w-24 mx-auto bg-gradient-to-r from-primary via-teal to-accent rounded-full mb-6"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {service.description}
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="mb-12 md:mb-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
              Key Benefits
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <FaCheckCircle className="text-primary dark:text-teal text-xl flex-shrink-0 mt-1" />
                  <p className="text-gray-700 dark:text-gray-300">{benefit}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-gradient-to-r from-primary to-teal dark:from-teal dark:to-primary p-12 rounded-2xl shadow-2xl mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss how CitiTekPro can help transform your business with our {service.title.toLowerCase()}.
            </p>
            <button
              onClick={() => scrollToSection('consultation')}
              className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-primary dark:text-teal font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              {service.cta}
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>

          {/* FAQ Section */}
          {service.faqs && service.faqs.length > 0 && (
            <div className="mb-12 md:mb-16">
              <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
                Frequently Asked Questions
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {service.faqs.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          )}

          {/* Minnesota Local Focus */}
          <div className="bg-white dark:bg-gray-800 p-8 md:p-10 rounded-2xl shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <FaMapMarkerAlt className="text-2xl text-primary dark:text-teal flex-shrink-0" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                Serving Minneapolis, Saint Paul & All of Minnesota
              </h2>
            </div>
            <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              CitiTekPro is headquartered at <strong>2910 Pillsbury Ave, Suite 31, Minneapolis, MN 55408</strong> — proud to be a local Minnesota business serving clients across the Twin Cities metro and greater Minnesota.
            </p>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              We serve businesses in Minneapolis, Saint Paul, Bloomington, Brooklyn Park, Brooklyn Center, Plymouth, Maple Grove, Richfield, Burnsville, Eagan, Edina, Roseville, and communities throughout Minnesota including the Karmel Mall area, Lake Street corridor, and Cedar-Riverside.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceLandingPage;
